import { FC, useMemo } from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompanyAutocomplete, CompanyOption } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import { ProductFormProductCompany } from "../../../ProductForm.types";
import { Banner } from "@foodpilot/foods";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

interface AssociatedStructuresFieldProps {
    ownedBy: { "@id": string; id: number; name: string } | null;
    value: ProductFormProductCompany[];
    onChange: (companies: ProductFormProductCompany[]) => void;
}

export const AssociatedStructuresField: FC<AssociatedStructuresFieldProps> = ({ ownedBy, value, onChange }) => {
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();

    const selectedCompanyOptions = useMemo(() => value.map((pc) => pc.organization), [value]);
    const excludeIds = useMemo(
        () =>
            ownedBy ?
                [ownedBy.id, ...selectedCompanyOptions.map((c) => c.id)]
            :   selectedCompanyOptions.map((c) => c.id),
        [ownedBy, selectedCompanyOptions],
    );

    const handleAddCompany = (newCompanies: CompanyOption[]) => {
        const newProductCompanies = newCompanies.map((company) => ({
            organization: company,
        }));
        onChange([...value, ...newProductCompanies]);
    };

    const handleRemoveCompany = (companyIdToRemove: number) => {
        onChange(value.filter((pc) => pc.organization.id !== companyIdToRemove));
    };

    return (
        <Stack flexDirection={"column"} gap={"16px"}>
            <Typography variant="body-semibold">
                {t("product.associated_organizations", "Associated organizations")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {selectedCompanyOptions.length === 0 ?
                    <Chip
                        label={t("product.no_associated_organizations", "No organizations selected")}
                        variant="outlined"
                        sx={{ color: "text.secondary" }}
                    />
                :   selectedCompanyOptions.map((company) => (
                        <Chip key={company.id} label={company.name} onDelete={() => handleRemoveCompany(company.id)} />
                    ))
                }
            </Box>

            <CompanyAutocomplete
                value={[]}
                onChange={handleAddCompany}
                placeholder={t("product.choose_organizations_placeholder", "Choose organizations")}
                excludeIds={excludeIds}
                hierarchyRootId={selectedCompany?.id}
            />

            <Banner variant={"themed"}>
                <Typography variant="body-regular">
                    {t(
                        "product.associated_organizations_help",
                        "Chosen organizations will have access to the product.",
                    )}
                </Typography>
            </Banner>
        </Stack>
    );
};
